/**
 * Resend Service
 *
 * Handles report resend operations — listing processed/error files
 * and initiating resends via the resend Lambda.
 */

import apiClient from './api';

/**
 * Get files available for resend for a given report and date.
 * @param {string} reportId - Report ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<{processed_files: Array, error_files: Array}>}
 */
export const getResendFiles = async (reportId, date) => {
  try {
    const response = await apiClient.get(`/reports/${reportId}/resend-files`, {
      params: { date }
    });
    const data = response.data?.data || response.data;
    return {
      processed_files: (data.processed_files || []).map(f => ({
        s3Key: f.s3_key,
        filename: f.filename,
        originalFilename: f.original_filename,
        sizeBytes: f.size_bytes,
        lastModified: f.last_modified,
        source: 'processed'
      })),
      error_files: (data.error_files || []).map(f => ({
        s3Key: f.s3_key,
        filename: f.filename,
        originalFilename: f.original_filename,
        sizeBytes: f.size_bytes,
        lastModified: f.last_modified,
        errorLog: f.error_log || '',
        source: 'error'
      }))
    };
  } catch (error) {
    console.error('Failed to fetch resend files:', error);
    throw error;
  }
};

/**
 * Initiate a report resend.
 * @param {string} reportId - Report ID
 * @param {Object} payload - Resend payload
 * @param {string} payload.source_s3_key - S3 key of the file to resend
 * @param {string} payload.recipient_type - "all" or "selected"
 * @param {string[]} [payload.email_ids] - Email IDs (when selected)
 * @param {string[]} [payload.group_ids] - Group IDs (when selected)
 * @returns {Promise<Object>}
 */
export const initiateResend = async (reportId, payload) => {
  try {
    const response = await apiClient.post(`/reports/${reportId}/resend`, payload);
    return response.data?.data || response.data;
  } catch (error) {
    console.error('Failed to initiate resend:', error);
    throw error;
  }
};
